import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Avatar, IconButton, Typography, Slide } from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { AuthContext } from '../../context/Auth/AuthContext';
import { usePlanPermissions } from '../../context/PlanPermissionsContext';
import { canSeeTicket, shouldNotifyForTicket } from '../../utils/ticketPermissions';

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'fixed',
    top: 24,
    right: 24,
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    maxWidth: 360,
    pointerEvents: 'none',
  },
  toast: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 16px',
    backgroundColor: theme.palette.type === 'dark' ? '#2d2d2d' : '#ffffff',
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    cursor: 'pointer',
    pointerEvents: 'auto',
    transition: 'all 0.3s ease',
    border: `1px solid ${theme.palette.type === 'dark' ? '#404040' : '#e0e0e0'}`,
    '&:hover': {
      transform: 'translateX(-4px)',
      boxShadow: '0 6px 24px rgba(0, 0, 0, 0.2)',
    },
  },
  toastTransfer: {
    borderLeft: '4px solid #4caf50',
  },
  toastMessage: {
    borderLeft: '4px solid #2196f3',
  },
  avatar: {
    width: 44,
    height: 44,
    backgroundColor: theme.palette.primary.main,
    fontSize: 16,
    fontWeight: 600,
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? '#fff' : '#1a1a1a',
    marginBottom: 2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  subtitle: {
    fontSize: 12,
    color: theme.palette.type === 'dark' ? '#b0b0b0' : '#666',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  closeButton: {
    padding: 4,
    marginLeft: 4,
    color: theme.palette.type === 'dark' ? '#888' : '#999',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? '#404040' : '#f5f5f5',
    },
  },
  badge: {
    fontSize: 10,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 10,
    marginBottom: 4,
    display: 'inline-block',
  },
  badgeTransfer: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  badgeMessage: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
  },
}));

const MAX_VISIBLE = 3;
const MAX_QUEUE = 50;

const NotificationToast = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const { planActive } = usePlanPermissions();
  const [notifications, setNotifications] = useState([]);
  const lastTicketUserIdRef = useRef({});
  const queueRef = useRef([]);

  const processRemoval = useCallback((id) => {
    setNotifications((prev) => {
      const next = prev.filter((n) => n.id !== id);
      if (next.length < MAX_VISIBLE && queueRef.current.length > 0) {
        const payload = queueRef.current.shift();
        const newId = Date.now() + Math.random();
        setTimeout(() => processRemoval(newId), 6000);
        return [...next, { ...payload, id: newId, addedAt: Date.now() }];
      }
      return next;
    });
  }, []);

  const removeNotification = useCallback(
    (id) => {
      processRemoval(id);
    },
    [processRemoval]
  );

  const addNotification = useCallback(
    (notification) => {
      if (!planActive) return;
      const id = Date.now() + Math.random();
      const now = Date.now();
      setNotifications((prev) => {
        const recentIdx = prev.findIndex(
          (n) => n.ticketId === notification.ticketId && n.addedAt && now - n.addedAt < 1500
        );
        if (recentIdx >= 0) {
          const replaced = [...prev];
          replaced[recentIdx] = { ...notification, id, addedAt: now };
          setTimeout(() => processRemoval(id), 6000);
          return replaced;
        }
        if (prev.length < MAX_VISIBLE) {
          setTimeout(() => processRemoval(id), 6000);
          return [...prev, { ...notification, id, addedAt: now }];
        }
        if (queueRef.current.length >= MAX_QUEUE) {
          queueRef.current.shift();
        }
        queueRef.current.push(notification);
        return prev;
      });
    },
    [processRemoval, planActive]
  );

  const handleNotificationClick = useCallback(
    (notification) => {
      const ticketId = notification.ticketUuid || notification.ticketId;
      if (ticketId) {
        history.push(`/tickets/conversation/${ticketId}`);
      }
      removeNotification(notification.id);
    },
    [removeNotification, history]
  );

  useEffect(() => {
    if (!user?.companyId || !socket || typeof socket.on !== 'function') return;

    const companyId = user.companyId;

    const handleTicket = (data) => {
      if (data.action !== 'update' && data.action !== 'create') return;

      const ticket = data.ticket;
      if (!ticket) return;

      if (ticket.isGroup) return;
      if (!canSeeTicket(user, ticket, null, user?.ticketWhatsappIds ?? [])) return;

      const ticketId = ticket?.id;
      const prevUserId = lastTicketUserIdRef.current[ticketId];
      const currentUserId = ticket.userId ?? null;

      const isTransferToMe =
        data.action === 'update' && currentUserId === user?.id && prevUserId !== user?.id;

      const isNewPendingTicket =
        data.action === 'create' && ticket.status === 'pending' && !ticket.userId;

      lastTicketUserIdRef.current[ticketId] = currentUserId;

      if (!isTransferToMe && !isNewPendingTicket) return;

      const contact = ticket?.contact;
      addNotification({
        type: 'transfer',
        title: contact?.name || 'Contato sem nome',
        subtitle: isTransferToMe ? 'Você recebeu uma transferência' : 'Novo ticket aguardando',
        avatar: contact?.profilePicUrl || contact?.urlPicture,
        ticketId: ticket?.id,
        ticketUuid: ticket?.uuid,
        contactId: contact?.id,
      });
    };

    const handleMessage = (data) => {
      if (data.action !== 'create') return;
      if (data.message?.fromMe) return;

      const ticket = data.ticket;
      const message = data.message;
      if (!message || !ticket) return;

      if (!shouldNotifyForTicket(user, ticket)) return;

      const ticketId = ticket.id ?? message.ticketId;
      const contact = ticket.contact ?? message.contact;
      const isGroup = ticket.isGroup ?? message.ticket?.isGroup ?? false;
      if (isGroup) return;
      if (!canSeeTicket(user, ticket, null, user?.ticketWhatsappIds ?? [])) return;

      let messageBody = message?.body || 'Nova mensagem';
      const mt = (message?.mediaType || '').toLowerCase();
      if (!messageBody.trim()) {
        if (mt === 'audio' || mt.includes('audio')) messageBody = 'Áudio';
        else if (mt === 'image' || mt.includes('image')) messageBody = 'Imagem';
        else if (mt === 'video' || mt.includes('video')) messageBody = 'Vídeo';
        else if (message?.mediaUrl) messageBody = 'Arquivo';
      }
      if (messageBody.length > 50) {
        messageBody = messageBody.substring(0, 50) + '...';
      }
      const contactName = contact?.name || contact?.pushname || 'Contato';
      addNotification({
        type: 'message',
        title: contactName,
        subtitle: messageBody,
        avatar: contact?.profilePicUrl || contact?.urlPicture,
        ticketId: ticketId || message.ticketId,
        ticketUuid: ticket?.uuid ?? message.ticket?.uuid,
        contactId: contact?.id,
      });
    };

    socket.on(`company-${companyId}-ticket`, handleTicket);
    socket.on(`company-${companyId}-appMessage`, handleMessage);

    return () => {
      socket.off(`company-${companyId}-ticket`, handleTicket);
      socket.off(`company-${companyId}-appMessage`, handleMessage);
    };
  }, [user, socket, addNotification]);

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className={classes.container}>
      {notifications.map((notification) => (
        <Slide key={notification.id} direction="left" in={true} mountOnEnter unmountOnExit>
          <div
            className={`${classes.toast} ${
              notification.type === 'transfer' ? classes.toastTransfer : classes.toastMessage
            }`}
            onClick={() => handleNotificationClick(notification)}
          >
            <Avatar src={notification.avatar} className={classes.avatar}>
              {getInitials(notification.title)}
            </Avatar>
            <div className={classes.content}>
              <span
                className={`${classes.badge} ${
                  notification.type === 'transfer' ? classes.badgeTransfer : classes.badgeMessage
                }`}
              >
                {notification.type === 'transfer' ? 'Transferência' : 'Mensagem'}
              </span>
              <Typography className={classes.title}>{notification.title}</Typography>
              <Typography className={classes.subtitle}>{notification.subtitle}</Typography>
            </div>
            <IconButton
              className={classes.closeButton}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                removeNotification(notification.id);
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </Slide>
      ))}
    </div>
  );
};

export default NotificationToast;
